// queue-http.interceptor.ts
import { Injectable } from '@angular/core';
import {
  HttpInterceptor, HttpRequest, HttpHandler, HttpEvent,
  HttpContextToken
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, finalize, shareReplay } from 'rxjs/operators';

// Context options
export const SKIP_QUEUE   = new HttpContextToken<boolean>(() => false); // ne pas mettre en file
export const SKIP_DEDUPE  = new HttpContextToken<boolean>(() => false); // ne pas dédupliquer (GET)
export const READ_LIMIT   = 8;  // lis max en // (monte à 8–10 en h2/h3, 6 si http/1.1)
export const WRITE_LIMIT  = 2;  // écris max en //

class Gate {
  constructor(public max = 6) {}
  private active = 0;
  private q: Array<() => void> = [];
  acquire(): Promise<void> {
    if (this.active < this.max) { this.active++; return Promise.resolve(); }
    return new Promise(res => this.q.push(() => { this.active++; res(); }));
  }
  release() {
    this.active = Math.max(0, this.active - 1);
    this.q.shift()?.();
  }
}

@Injectable()
export class QueueHttpInterceptor implements HttpInterceptor {
  // 1 pool READ et 1 pool WRITE par origine (schéma+host+port)
  private gates = new Map<string, Gate>();
  // Dédup des GET: clé -> observable partagé
  private inflight = new Map<string, Observable<HttpEvent<unknown>>>();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const method = req.method.toUpperCase();
    const isRead = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';

    // ---------- Déduplication (GET uniquement) ----------
    const allowDedupe = isRead && !req.context.get(SKIP_DEDUPE);
    const dedupeKey = allowDedupe ? `${method} ${req.urlWithParams}` : null;

    if (dedupeKey) {
      const hit = this.inflight.get(dedupeKey);
      if (hit) return hit as Observable<HttpEvent<any>>;
    }

    // ---------- File d'attente (sémaphore) ----------
    const skipQueue = req.context.get(SKIP_QUEUE);
    const send$ = next.handle(req);

    const stream$ = skipQueue ? send$ : from(this.acquireGate(req, isRead).acquire()).pipe(
      switchMap(() => send$),
      finalize(() => this.acquireGate(req, isRead).release())
    );

    // Partage la réponse si dédup
    const out$ = (dedupeKey ? stream$.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inflight.delete(dedupeKey!))
    ) : stream$) as Observable<HttpEvent<any>>;

    if (dedupeKey) this.inflight.set(dedupeKey, out$);
    return out$;
  }

  private acquireGate(req: HttpRequest<any>, isRead: boolean): Gate {
    const u = new URL(req.url, location.origin); // gère aussi les URLs relatives
    const key = `${u.origin}|${isRead ? 'READ' : 'WRITE'}`;
    let g = this.gates.get(key);
    if (!g) {
      g = new Gate(isRead ? READ_LIMIT : WRITE_LIMIT);
      this.gates.set(key, g);
    }
    return g;
  }
}
